import * as Inovx from '@shared';
import OntologyConstants from '../constants/OntologyConstants';
import FacilityStore from '../stores/FacilityStore';
import FacilityServices from '../services/FacilityServices';
import FacilityActions from './FacilityActions';
import FacilityConstants from '../constants/FacilityConstants';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;

class OntologyActions {
    constructor() {
    }

    beginEditOntology(): void {
        this._dispatchAction(OntologyConstants.ONTOLOGY_BEGIN_EDIT);
    }

    finishEditOntology(): void {
        this._dispatchAction(OntologyConstants.ONTOLOGY_FINISH_EDIT);
    }

    setOntologyFormDirty(dirty: boolean): void {
        this._dispatchAction(OntologyConstants.ONTOLOGY_SET_DIRTY, { dirty });
    }

    async createOntologyPart(data: { Name: string, ParentID: string }, preRefreshCallback?: (newPartID: string) => void) {
        if (!SystemStore.isActionInProgress(OntologyConstants.ONTOLOGY_PART_ADD)) {
            SystemActions.beginAction(OntologyConstants.ONTOLOGY_PART_ADD);

            try {
                const facility = FacilityStore.getSelectedFacility();
                const newHierarchyPart: IOntologyPart = {
                    ID: "",
                    VersionToken: "",
                    Name: data.Name.trim(),
                    FacilityID: facility.ID,
                    ContentTypes: [],
                    OntologyParentID: data.ParentID,
                    TransformationType: "inherit",
                    MapType: "inherit",
                    GpsTransformationType: "none",
                    GpsTransformation: null,
                    ContentExtentsMin: null,
                    ContentExtentsMax: null,
                    IsRoot: false,
                };
                const result = await FacilityServices.createOntologyPart(newHierarchyPart);

                preRefreshCallback?.(result.ID);

                // refresh data
                await FacilityActions.openFacilityDetail(facility.ID);
            }
            finally {
                SystemActions.endAction(OntologyConstants.ONTOLOGY_PART_ADD);
            }
        }       
    }

    async editOntologyPart(part: IOntologyPart) {
        if (!SystemStore.isActionInProgress(OntologyConstants.ONTOLOGY_PART_EDIT)) {
            SystemActions.beginAction(OntologyConstants.ONTOLOGY_PART_EDIT);

            try {
                part.Name = part.Name.trim();
                if (part.MapType === "inherit") {
                    part.Map = null;
                } 
                if (part.Map) {
                    this._recalculateSiteMap(part.Map);
                }
                
                await FacilityServices.editOntologyPart(part);
                this.setOntologyFormDirty(false);

                // refresh data
                await FacilityActions.openFacilityDetail(FacilityStore.getSelectedFacility().ID);
            }
            finally {
                SystemActions.endAction(OntologyConstants.ONTOLOGY_PART_EDIT);
            }
        }
    }

    async deleteOntologyPart(part: IOntologyPart) {
        if (!SystemStore.isActionInProgress(OntologyConstants.ONTOLOGY_PART_DELETE)) {
            SystemActions.beginAction(OntologyConstants.ONTOLOGY_PART_DELETE);

            try {
                await FacilityServices.deleteOntologyPart(part);

                // refresh data
                await FacilityActions.openFacilityDetail(FacilityStore.getSelectedFacility().ID);
            }
            finally {
                SystemActions.endAction(OntologyConstants.ONTOLOGY_PART_DELETE);
            }
        }
    }

    async getOntologyGraphicSources(ontologyID: string) {
        const data = await FacilityServices.getGraphicSources(ontologyID);
        if (data.length > 0) data.sort((a, b) => a.Name.localeCompare(b.Name));
        return data;
    }
    async deleteOntologyGraphicSource(part: IOntologyGraphicSource) {
        if (!SystemStore.isActionInProgress(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE)) {
            SystemActions.beginAction(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE);

            try {
                return await FacilityServices.deleteOntologyGraphicSource(part);
            }
            finally {
                SystemActions.endAction(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE);
            }
        }
    }

    async deleteOntologyGraphicSourceType(part: IOntologyGraphicSourceType) {
        if (!SystemStore.isActionInProgress(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE)) {
            SystemActions.beginAction(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE);

            try {
                return await FacilityServices.deleteOntologyGraphicSourceType(part);
            }
            finally {
                SystemActions.endAction(OntologyConstants.ONTOLOGY_GRAPHIC_SOURCE_DELETE);
            }
        }
    }

    selectOntology(ontology: IOntologyPart) {
        this._dispatchAction(OntologyConstants.ONTOLOGY_SELECT, { ontology} );
    }

    verifyOntologyModalChanges(): boolean {
        if (FacilityStore.getOntologyModalVisible()) {
            if (FacilityStore.getOntologyFormDirty()) {
                SystemActions.displayInfoMessage({
                    message: InovxLoader.t('facility:message.savePendingChangesInfo')
                });
                return false;
            }
        }
        return true;
    }
    verifyAndCloseOntologyModalChanges(): boolean {
        var verified = true;
        if (FacilityStore.getOntologyModalVisible()) {
            verified = this.verifyOntologyModalChanges();
            if (verified) {
                this.finishEditOntology();
            }
        }
        return verified;
    }

    private _recalculateSiteMap(siteMap: ISiteMap) {
        const dxWorld = siteMap.WorldScale.X - siteMap.WorldTranslation.X;
        const dyWorld = siteMap.WorldScale.Y - siteMap.WorldTranslation.Y;
        const dxImage = siteMap.ImageScale.X - siteMap.ImageTranslation.X;
        const dyImage = siteMap.ImageScale.Y - siteMap.ImageTranslation.Y;
        if (dxImage !== 0 || dyImage !== 0) {
            const scale = Math.sqrt((dxWorld * dxWorld) + (dyWorld * dyWorld)) / Math.sqrt((dxImage * dxImage) + (dyImage * dyImage));
            if (scale) {
                const rotation = -(Math.atan2(dyWorld, dxWorld) - Math.atan2(dyImage, dxImage));
                const rotatedX = siteMap.WorldTranslation.X * Math.cos(rotation) - siteMap.WorldTranslation.Y * Math.sin(rotation);
                const rotatedY = siteMap.WorldTranslation.X * Math.sin(rotation) + siteMap.WorldTranslation.Y * Math.cos(rotation);

                siteMap.Origin.X = siteMap.ImageTranslation.X - rotatedX / scale;
                siteMap.Origin.Y = siteMap.ImageTranslation.Y - rotatedY / scale;
                siteMap.Scale = scale;
                siteMap.Rotation = rotation;

                siteMap.RealWorldMin = this._imageToWorld(siteMap, { X: -siteMap.ImageSize.X / 2, Y: -siteMap.ImageSize.Y / 2 });
                siteMap.RealWorldMax = this._imageToWorld(siteMap, { X: siteMap.ImageSize.X / 2, Y: siteMap.ImageSize.Y / 2 });

                return;
            }
        }

        siteMap.Origin = { X: 0, Y: 0 };
        siteMap.Scale = 0;
        siteMap.Rotation = 0;
    }

    private _imageToWorld(siteMap: ISiteMap, imgPoint: IVector2D): IVector2D {
        // translate image point back to the (scaled+rotated) world frame
        const dx = imgPoint.X - siteMap.Origin.X;
        const dy = imgPoint.Y - siteMap.Origin.Y;

        // undo the “divide by scale” that was applied when mapping world→image
        const scaledX = dx * siteMap.Scale;
        const scaledY = dy * siteMap.Scale;

        // apply inverse rotation (Rᵀ), since the forward transform did R * world / scale + Origin
        const c = Math.cos(siteMap.Rotation);
        const s = Math.sin(siteMap.Rotation);

        // Rᵀ = [ c  s
        //       -s  c ]
        const worldX = scaledX * c + scaledY * s;
        const worldY = -scaledX * s + scaledY * c;

        return { X: worldX, Y: worldY };
    }

    private _dispatchAction(actionType: string, data?: any) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: actionType,
            ...data
        });
    }
    
}

const ontologyActions = new OntologyActions();

export default ontologyActions;